import React, { Component } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { MobileTruckLoadContext } from './MobileTruckLoadContext';
// Reverted - restricting POSSIBLE_FORMATS to a guessed subset (Code 128/39)
// made the camera stop recognizing barcodes entirely, because the real label
// format actually in use here was never confirmed first. No hints passed now
// means ZXing's default of trying every supported format again - slower per
// frame, but correct. Narrowing this back down is worth revisiting, but only
// once the actual barcode symbology these labels use is confirmed first.
const BARCODE_SCAN_HINTS = undefined;


const ALERT_ICONS = { valid: '✔', duplicate: '⚠', wrong: '✕', notfound: '?', info: 'ℹ' };
const SCANNER_SPEED_THRESHOLD_MS = 300;
// Some scanners are configured with no Enter/Tab suffix at all - the decoded code
// just sits in the box forever otherwise, waiting for a manual Enter/Submit tap.
// If nothing new arrives for this long right after a scanner-speed burst, treat
// the scan as finished and submit it automatically. Gated on scanner-speed timing
// (see SCANNER_SPEED_THRESHOLD_MS) so a human pausing mid-type never gets cut off -
// nobody types a whole barcode within this window, only a scanner does.
const AUTO_SUBMIT_PAUSE_MS = 100;
// With only 1-2 characters typed, "elapsed since the first keystroke" is barely
// any time at all regardless of who's typing - a human's very first character
// followed by a completely normal thinking-pause looks identical to a finished
// scanner burst by that measure alone, since there's nothing yet to tell a real
// burst apart from "nothing has happened yet". No real barcode/HU code is this
// short, so require at least this many characters before the pause-timer is
// even allowed to auto-submit - the elapsed check above still has to pass too.
const MIN_AUTO_SUBMIT_LENGTH = 4;
// Scan mode's own input (unlike Manual Entry's) never has a human-typing case to
// rule out, so its auto-submit just waits for this short a quiet pause after the
// last keystroke - no elapsed-since-first-keystroke speed check needed, just long
// enough that a still-in-progress injection isn't cut off mid-code.
const SCAN_INPUT_AUTO_SUBMIT_MS = 150;
// Was 400ms with no explanation - purely artificial wait, since
// processScanOutcome is a fast synchronous local lookup with nothing to actually
// wait on. Kept non-zero only as a tiny buffer against a truly double-fired scan
// landing mid-way through scanPhase flipping back to 'idle'.
const SCAN_PROCESS_DELAY_MS = 80;

// getUserMedia/zxing failures are DOMExceptions with a specific .name - mapped
// here to an accurate, actionable message instead of the one blanket "needs
// HTTPS" string startCameraScan used to show for EVERY failure reason,
// including several (permission denied, no camera, camera already in use)
// that have nothing to do with the protocol at all. That old message kept
// showing up even in an already-HTTPS production environment, because it
// never actually looked at what the error was - see startCameraScan's own
// isSecureContext check for the one case that genuinely IS about HTTPS.
function cameraErrorMessage(e) {
    var name = e && e.name;
    if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        // Once a mobile browser has denied a site's camera permission, it does
        // NOT show the Allow/Deny popup again on a retry - getUserMedia just
        // silently rejects with this same error every time. Desktop Chrome
        // still shows a clickable blocked-camera icon in the address bar to
        // fix this, but there's no equivalent affordance in the page itself on
        // mobile, so the message has to tell the operator exactly where to go
        // instead of repeating "please allow" with nothing left to click.
        return 'Camera access is blocked for this site - re-enable it manually: tap the ⓘ or 🔒 icon next to the web address, then Permissions (or Site settings) → Camera → Allow, and reload this page. On some phones this is instead under your phone\'s own Settings → Apps → [this browser] → Permissions → Camera.';
    }
    if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
        return 'No camera was found on this device.';
    }
    if (name === 'NotReadableError' || name === 'TrackStartError') {
        return 'The camera could not be started - it may already be in use by another app.';
    }
    if (name === 'OverconstrainedError' || name === 'ConstraintNotSatisfiedError') {
        return 'No camera on this device matches what scanning needs.';
    }
    if (name === 'SecurityError') {
        return 'Camera access needs HTTPS (or localhost) - this page is not loaded over a secure connection.';
    }
    return 'Could not access the camera - check permissions and try again.';
}

// One fresh id per submitBarcode() call (i.e. per scan attempt, not per HU) -
// sent to the backend as scan_attempt_id so recordScan can recognize a
// network retry of THIS SAME attempt and just replay its original outcome,
// instead of running the request through the server-side race-claim logic a
// second time (which would otherwise look exactly like a second scanner
// racing for the same HU and losing - see recordScan's own comment). No
// crypto.randomUUID() dependency - some of the older handheld/tablet webviews
// this app runs on don't have it, and this doesn't need to be
// cryptographically random, just unique per attempt.
function generateScanAttemptId() {
    return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
}

// Vibration pattern (ms on/off) + beep pitch/length + spoken word per outcome -
// lets an operator who isn't looking at the screen (e.g. aiming a camera, or
// scanning mid-scroll) still tell a clean scan apart from an exception without
// reading the alert banner. Speech deliberately stays a short word, not the
// full alert sentence - faster to say, easier to tell apart at a glance of the
// ear during rapid consecutive scans.
// `speech` is {en, hi} so playScanFeedback can pick based on the operator's
// chosen voice language (see this.state.voiceLang) - everything else (vibrate
// pattern, beep freq/duration) stays language-independent.
const SCAN_FEEDBACK = {
    valid: { vibrate: 60, freq: 1400, duration: 0.12, speech: { en: 'Scanned', hi: 'स्कैन हो गया' } },
    duplicate: { vibrate: [40, 40, 40], freq: 700, duration: 0.16, speech: { en: 'Duplicate', hi: 'डुप्लिकेट' } },
    wrong: { vibrate: [80, 60, 80], freq: 300, duration: 0.25, speech: { en: 'Wrong truck', hi: 'गलत ट्रक' } },
    notfound: { vibrate: [40, 40, 40, 40], freq: 500, duration: 0.16, speech: { en: 'Not found', hi: 'नहीं मिला' } },
    // Same lowest-pitch/longest-buzz treatment as 'wrong' - this is refused
    // before it's even classified, but it's just as important the operator
    // hears it without having to look at the screen (see submitBarcode's
    // offline guard).
    offline: { vibrate: [80, 60, 80], freq: 300, duration: 0.25, speech: { en: 'No connection', hi: 'कनेक्शन नहीं है' } },
    // A scan attempt actually reached the network layer and failed (no response
    // came back at all) - distinct speech from 'offline' so an operator who
    // knows their wifi icon looks fine isn't told "no connection" when the real
    // problem is the API being unreachable behind it (see recordScanReal).
    networkError: { vibrate: [80, 60, 80], freq: 300, duration: 0.25, speech: { en: 'Network error', hi: 'नेटवर्क में समस्या है' } },
    // A single short, higher-pitched buzz - deliberately the opposite shape of
    // every exception pattern above (all low-pitch, multi-buzz) - so this
    // reads as good news at a glance of the ear, not another problem (see
    // onGoOnline).
    online: { vibrate: 50, freq: 1000, duration: 0.15, speech: { en: 'Back online', hi: 'कनेक्शन वापस आ गया' } },
    // Played when a scan attempt is refused because an earlier alert still
    // needs acknowledgement (see maybeRepeatPendingAck). Deliberately NOT the
    // pending alert's own voice - replaying e.g. "Duplicate" on every blocked
    // attempt sounds like a verdict on whatever was JUST scanned (which was
    // never actually looked at - the attempt was refused outright), not a
    // replay of an older message about a DIFFERENT carton. This phrase makes
    // clear the block is procedural, not a judgment on the new scan.
    ackReminder: { vibrate: [30, 30, 30, 30, 30], freq: 900, duration: 0.1, speech: { en: 'Please acknowledge the alert before proceeding with scan', hi: 'स्कैन जारी रखने से पहले कृपया अलर्ट को स्वीकार करें' } }
};
const ALL_CARTONS_LOADED_SPEECH = {
    en: 'All cartons loaded. Tap Finish Loading to complete the trip.',
    hi: 'सभी कार्टन लोड हो गए हैं। ट्रिप पूरी करने के लिए फिनिश लोडिंग दबाएं।'
};
// A 'valid' result now waits on the server's race-safe claim before its real
// outcome tone plays (see submitBarcode) - without SOME immediate feedback,
// an operator who isn't looking at the screen would feel like the scan didn't
// register at all during that gap. Short/sharp/high on purpose, clearly
// distinct from every SCAN_FEEDBACK tone above (all much lower-pitched and
// longer) so it never gets mistaken for an actual outcome - it's just an
// "acknowledged, working on it" tick, not a result.
const PROCESSING_FEEDBACK = { vibrate: 12, freq: 2200, duration: 0.04 };
// How long a camera-decoded barcode has to be OUT of the last accepted scan's
// cooldown before it counts again - zxing calls back on every video frame it can
// read, so without this the same still-in-frame carton would fire dozens of times
// a second instead of once. Must be AT LEAST as long as the success alert stays
// on screen (showAlert's own 3000ms auto-dismiss) - this used to be 1500ms,
// shorter than that alert, so the still-in-frame carton would get re-decoded
// and resubmitted (as a false "duplicate") while its own success message was
// still showing, before the operator had any chance to move the camera away.
const CAMERA_SCAN_COOLDOWN_MS = 3200;

// Ported from test.html's screen2 (barcode scan flow). Manual entry / a real
// dedicated handheld scanner both funnel through the same input: a real scanner is a
// "keyboard wedge" - it types the decoded value into whatever input has focus, then
// sends Enter, indistinguishable at the DOM level from a person typing it and
// pressing Enter themselves. Two signals still separate them: clicking Submit is
// unambiguously a human (a scanner never clicks a button), and for Enter-terminated
// input, a scanner injects the whole code in a few milliseconds while a human takes
// a few hundred - timing from first keystroke to Enter tells the two apart.
//
// A third path - "Scan with Camera" - covers testers without a dedicated handheld:
// zxing decodes barcodes straight from getUserMedia video frames and feeds the
// result into this same submitBarcode pipeline, same as a keyboard-wedge scan.
// getUserMedia requires a secure context (HTTPS, or localhost) - won't prompt for
// camera permission over plain LAN-IP http.
export default class MobileScan extends Component {
    static contextType = MobileTruckLoadContext;

    constructor(props) {
        super(props);
        this.state = {
            barcodeValue: '',
            scanPhase: 'idle', // 'idle' | 'processing'
            // 'scan' keeps a real, visible, fully editable barcode input focused -
            // this device's scanner integration injects decoded text through
            // Android's IME/InputConnection layer (like most integrated handheld
            // scanners), the same channel the on-screen keyboard itself uses, into
            // whatever currently has real DOM focus. With nothing focused, or with
            // that connection cut off (inputMode="none", readOnly - both tried and
            // ruled out, see focusBarcodeInput), the scanner still beeps - that's
            // just the device confirming its own decode - but the keystrokes have
            // nowhere to land. 'manual' is the one place a human actually types.
            // "Scan with Camera" works in both.
            entryMode: 'scan', // 'scan' | 'manual'
            viewingSummary: false,
            detailsOpen: false,
            missingOpen: true,
            // needsAck (duplicate/wrong-truck/not-found/offline - see showAlert)
            // means this alert stays up until the operator explicitly presses OK
            // instead of auto-dismissing after 3s, and submitBarcode refuses every
            // further scan attempt while it's showing - operators were scanning
            // straight through an exception toast they hadn't actually registered
            // and re-scanning the same duplicate carton several times in a row
            // before ever noticing it. A genuine scan success never sets this, so
            // a clean run through a trip still isn't slowed down by a tap per carton.
            alert: { show: false, title: '', sub: '', cls: '', needsAck: false },
            cameraOpen: false,
            cameraError: '',
            // True only once ZXing has actually got a live video stream - the
            // green aim frame/sweep line is gated on this, not on cameraOpen,
            // so a camera that never started (permission blocked, server
            // Permissions-Policy, no camera) doesn't show an animated "scanning"
            // overlay over a black screen that can never decode anything.
            cameraStreaming: false,
            // navigator.onLine reflects the OS-level network adapter (airplane mode,
            // a disconnected wifi radio, etc.) - checked up front so scanning starts
            // blocked already if the device is offline right when this screen mounts,
            // not only once the 'offline' event fires for a connection lost afterward.
            isOffline: typeof navigator !== 'undefined' && navigator.onLine === false,
            // Distinct from isOffline - the browser can genuinely believe it has a
            // connection (wifi radio still associated) while the actual path to
            // the API is dead (e.g. connected to a hotspot's wifi after the phone
            // providing it has turned the hotspot off). navigator.onLine can't
            // catch that; only an actual failed request can. Set the moment any
            // truckload API call from this screen comes back with no response at
            // all (see recordScanReal returning {ok:false}) - cleared again by
            // acknowledgeAlert, same "unblock on OK, next attempt re-tests"
            // policy as isOffline, deliberately not a background retry loop.
            networkError: false
        };
        this.firstKeyTime = null;
        this.globalScanBuffer = '';
        this.globalScanFirstKeyTime = null;
        this.lastAckReminderAt = null;
        this.barcodeRef = React.createRef();
        this.videoRef = React.createRef();
        this.zxingReader = null;
        this.scanControls = null;
    }

    // Raises the SAME compulsory acknowledgement popup + voice every other
    // exception uses, the moment the connection actually drops - not just the
    // passive top banner, and not only once the operator happens to attempt a
    // scan. maybeRepeatPendingAck still applies here too - if something else
    // already needs acknowledgement, this just re-voices that instead of
    // stacking a second alert on top of it.
    raiseOfflineAlert = () => {
        if (this.maybeRepeatPendingAck()) return;
        this.showAlert('No Data Connection', "Scanning is paused until you're back online. Please check your connection.", 'wrong', true, 'offline');
        this.playScanFeedback('offline');
    };

    onGoOffline = () => { this.setState({ isOffline: true }); this.raiseOfflineAlert(); };
    // Voice always plays - purely audio, doesn't touch the alert, so it's safe
    // regardless of what else is on screen.
    //
    // The still-pending OFFLINE popup specifically is auto-dismissed here, not
    // left for a manual tap - unlike duplicate/wrong-truck/not-found (which
    // each represent a physical-world action the operator did that they must
    // consciously register), "offline" was only ever reporting a connectivity
    // STATUS. Once that status has genuinely resolved on its own, there's
    // nothing left for the operator to acknowledge, so it closes itself and
    // the "Back Online" toast replaces it in the same spot.
    //
    // Any OTHER needsAck alert (a duplicate, a wrong-truck, a still-pending
    // network-error from a failed write) is left completely alone - only the
    // voice plays for those, exactly as before. Auto-clearing those too would
    // be the same clobbering bug already fixed for a stale success toast (see
    // submitBarcode) - those still need a deliberate tap.
    onGoOnline = () => {
        this.setState({ isOffline: false });
        this.playScanFeedback('online');
        var alert = this.state.alert;
        var offlineAlertPending = alert.show && alert.needsAck && alert.feedbackType === 'offline';
        if (!alert.show || !alert.needsAck || offlineAlertPending) {
            this.showAlert('Back Online', 'Connection restored - scanning is available again.', 'valid', false);
        }
        if (offlineAlertPending) this.focusBarcodeInput();
    };

    componentDidMount() {
        // Independent of the session-resume flow below - the operator should see
        // the offline banner (and have scanning actually blocked, see
        // submitBarcode) from the moment this screen mounts, not only once a
        // session happens to be ready.
        window.addEventListener('offline', this.onGoOffline);
        window.addEventListener('online', this.onGoOnline);
        // The session lives only in MobileTruckLoadContext's React state - a
        // browser refresh wipes it completely. Rather than bouncing straight back
        // to Input Plan, try rebuilding the same live session straight from the
        // backend using transactionId/tripNo - now carried in the URL itself (see
        // the /lm-mobile/scan route params) precisely so a refresh has something
        // durable to rebuild from, instead of depending on this in-memory context
        // state surviving. Only Input Plan is still the fallback when there's
        // truly nothing to resume from (no session AND no ids in the URL, or the
        // ids don't resolve to any real plan data).
        if (!this.context.session.loadVehicle) {
            var { transactionId, tripNo } = this.props.match.params;
            if (transactionId && tripNo) {
                this.context.resumeSessionFromIds(transactionId, tripNo).then((opts) => {
                    if (opts) { this.afterSessionReady(); }
                    else { this.props.history.replace('/lm-mobile/input-plan'); }
                }).catch((e) => {
                    console.log('resumeSessionFromIds failed', e);
                    this.props.history.replace('/lm-mobile/input-plan');
                });
                return;
            }
            this.props.history.replace('/lm-mobile/input-plan');
            return;
        }
        this.afterSessionReady();
    }

    // Everything that only makes sense once ctx.session is actually populated -
    // split out from componentDidMount so the resumeSessionFromIds path above can
    // run this same setup once its fetch resolves, instead of duplicating it.
    afterSessionReady = () => {
        // A real handheld scanner has no way to click into the field itself - it
        // just sends keystrokes to whatever currently has focus - so keep this
        // input focused by default whenever the Scan screen is shown.
        this.focusBarcodeInput();
        // The mobile TTS engine behind speechSynthesis has a real cold-start cost
        // (5-10s on some Android browsers) the first time speak() is ever called -
        // every call after that is fast, since the engine's already warmed up.
        // Absorbing that cost here (silently, right on mount) means it's already
        // paid for by the time the operator's first real scan comes in, instead of
        // that first scan's own voice feedback lagging behind everything else.
        this.primeSpeech();
        // Fallback for when it doesn't stay focused - e.g. scrolling the page blurs
        // the input on mobile, and a scan that comes in at that moment would
        // otherwise land nowhere. See onGlobalScanKeyDown.
        document.addEventListener('keydown', this.onGlobalScanKeyDown);
        // This is the only input on the whole screen, so any tap elsewhere
        // (accordion headers, chips, the missing-HU list, anything) should hand
        // focus straight back to it instead of leaving it sitting on whatever was
        // just tapped - a hardware scanner has no way to click into the field
        // itself, so it needs this to actually be focused to receive its keystrokes
        // (the keydown fallback above only covers scans with nothing focused at
        // all, e.g. mid-scroll - a genuinely focused button is still a problem).
        document.addEventListener('click', this.onDocumentClickRefocus);
        // Covers arriving on this screen already offline (not just losing the
        // connection mid-session, which onGoOffline handles) - same popup+voice
        // either way, not just the passive banner.
        if (this.state.isOffline) this.raiseOfflineAlert();
    };

    componentDidUpdate(prevProps, prevState) {
        if (prevState.scanPhase === 'processing' && this.state.scanPhase === 'idle') {
            if (this.context.session.remainingHUs.length > 0) {
                this.focusBarcodeInput();
            } else if (this.state.cameraOpen) {
                // Camera scanning finished the trip via its own last decode -
                // nothing left to point it at, so close it same as the explicit
                // Close button would.
                this.closeCamera();
            }
        }
        // The <video> element only exists once cameraOpen renders it - start
        // decoding right after that mount, not before (the ref would still be
        // null). Skipped when openCamera's own permission pre-check already
        // found it denied and set cameraError itself - calling startCameraScan
        // anyway would just fail with the exact same error a second time.
        if (!prevState.cameraOpen && this.state.cameraOpen && !this.state.cameraError) {
            this.startCameraScan();
        }
    }

    componentWillUnmount() {
        clearTimeout(this.alertTimer);
        clearTimeout(this.autoSubmitTimer);
        clearTimeout(this.globalAutoSubmitTimer);
        clearTimeout(this.scanAutoSubmitTimer);
        clearTimeout(this.speakTimer);
        this.stopCameraScan();
        window.removeEventListener('offline', this.onGoOffline);
        window.removeEventListener('online', this.onGoOnline);
        document.removeEventListener('keydown', this.onGlobalScanKeyDown);
        document.removeEventListener('click', this.onDocumentClickRefocus);
        // Don't let a queued/in-progress "Duplicate"/"Wrong truck" utterance keep
        // talking after the operator has already navigated to a different screen.
        try { if ('speechSynthesis' in window) window.speechSynthesis.cancel(); } catch (e) { /* ignore */ }
    }

    // Runs after every click anywhere on the screen. Deferred one tick so it wins
    // against whatever focus change the click itself just caused (e.g. a tapped
    // button natively taking focus) - focusBarcodeInput already no-ops correctly
    // on its own (nothing to focus, or nothing left to scan), so this only needs
    // to skip the cases where stealing focus back would actually be wrong: the
    // camera overlay being open (input's hidden/irrelevant then), and the input
    // itself being what was just tapped (focus is already exactly where it should be).
    onDocumentClickRefocus = (e) => {
        // Also skips the Voice Language <select> - without this, clicking it
        // to open the dropdown immediately lost focus back to the barcode
        // input on the next tick, which closes/prevents a native <select>'s
        // dropdown from opening at all. tagName check instead of a specific
        // ref since this is the only <select> on this screen; any select
        // (now or added later) should naturally be exempt from this for the
        // same reason.
        if (this.state.cameraOpen || e.target === this.barcodeRef.current || e.target.tagName === 'SELECT') return;
        setTimeout(() => this.focusBarcodeInput(), 0);
    };

    // A real handheld scanner just types into whatever has focus, then sends
    // Enter - if the barcode input lost focus (e.g. the user was scrolling the
    // page, which blurs it on mobile) right when a scan came in, those keystrokes
    // land nowhere and the scan is silently lost. This is a fallback for exactly
    // that: only when nothing editable currently has focus (the input's own
    // onChange/onBarcodeKeyDown below already handles the normal case, so skip
    // here to avoid double-processing the same keystrokes), buffer characters at
    // the document level instead and submit on Enter - same result either way.
    //
    // Only actually useful for a true Bluetooth HID keyboard-mode scanner, which
    // sends real per-character key events. Confirmed (via a temporary debug log)
    // that this device's own scanner integration injects text through Android's
    // IME layer instead - its keydowns report key:"Unidentified" with no actual
    // character in them at all, focused or not, so this buffer can never
    // reconstruct a real barcode from them. Left in place as a safety net for
    // genuine HID scanners in the field; this device's scans are only ever
    // captured through the input's own onChange (e.target.value), never here.
    onGlobalScanKeyDown = (e) => {
        var active = document.activeElement;
        var isEditable = active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable);
        if (isEditable) return;

        if (e.key === 'Enter') {
            e.preventDefault();
            clearTimeout(this.globalAutoSubmitTimer);
            var code = this.globalScanBuffer;
            this.globalScanBuffer = '';
            if (!code) return;
            var elapsed = this.globalScanFirstKeyTime !== null ? (Date.now() - this.globalScanFirstKeyTime) : Infinity;
            this.globalScanFirstKeyTime = null;
            this.submitBarcode('scanner', code, elapsed <= SCANNER_SPEED_THRESHOLD_MS);
            return;
        }
        // Ignore modifier/navigation keys (Shift, Tab, Escape, arrows, etc.) - only
        // buffer single printable characters, same as what a scanner actually types.
        if (e.key.length === 1) {
            if (this.globalScanBuffer === '') { this.globalScanFirstKeyTime = Date.now(); }
            this.globalScanBuffer += e.key;
            // Mirrored into state so the box visibly fills in the same way it would
            // if the input itself had been focused for this scan - keeps the two
            // paths looking identical to the user, not just functionally equivalent.
            this.setState({ barcodeValue: this.globalScanBuffer });
            e.preventDefault();
            this.armGlobalAutoSubmit();
        }
    };

    // Same no-terminator fallback as armAutoSubmit, just for the unfocused/global
    // capture buffer instead of the input's own state.
    armGlobalAutoSubmit = () => {
        clearTimeout(this.globalAutoSubmitTimer);
        this.globalAutoSubmitTimer = setTimeout(() => {
            var code = this.globalScanBuffer;
            var elapsed = this.globalScanFirstKeyTime !== null ? (Date.now() - this.globalScanFirstKeyTime) : Infinity;
            if (code.length >= MIN_AUTO_SUBMIT_LENGTH && elapsed <= SCANNER_SPEED_THRESHOLD_MS) {
                this.globalScanBuffer = '';
                this.globalScanFirstKeyTime = null;
                this.submitBarcode('scanner', code, true);
            }
        }, AUTO_SUBMIT_PAUSE_MS);
    };

    focusBarcodeInput = () => {
        // Focuses regardless of mode now - Scan mode's input needs real focus just
        // as much as Manual Entry's does for a scanner's injected keystrokes to
        // land anywhere at all (see the state comment).
        //
        // Both inputMode="none" AND readOnly-until-focused were tried and ruled
        // out for hiding the keyboard here - this device's scanner integration
        // injects decoded text through Android's IME/InputConnection layer, and
        // BOTH of those tricks cut that connection off entirely rather than just
        // hiding the visible keyboard UI (readOnly in particular blocks
        // InputConnection outright on Android, not just the keyboard popup - so
        // nothing the scanner injected ever reached the field). A plain, fully
        // editable, focused input is what this scanner needs to work at all;
        // keyboard suppression has to be solved a different way (e.g. reacting to
        // window.visualViewport instead of hiding the keyboard), not here.
        if (this.barcodeRef.current && this.context.session.remainingHUs.length > 0) {
            this.barcodeRef.current.focus();
        }
    };

    // Switching modes clears any in-progress partial code (both the visible
    // input's and the hidden global-capture buffer's) rather than letting a
    // half-typed/half-scanned code silently carry over into the other mode.
    setEntryMode = (mode) => {
        if (mode === this.state.entryMode) return;
        clearTimeout(this.autoSubmitTimer);
        clearTimeout(this.globalAutoSubmitTimer);
        clearTimeout(this.scanAutoSubmitTimer);
        this.firstKeyTime = null;
        this.globalScanBuffer = '';
        this.globalScanFirstKeyTime = null;
        this.setState({ entryMode: mode, barcodeValue: '' });
        // Blur the outgoing input first - switching away from Manual Entry needs
        // this to actually dismiss its on-screen keyboard - then refocus once the
        // new mode's input (same ref) has mounted, so the newly-shown field is
        // ready to receive keystrokes right away instead of needing an extra tap.
        if (this.barcodeRef.current) { this.barcodeRef.current.blur(); }
        setTimeout(() => this.focusBarcodeInput(), 0);
    };

    openCamera = async () => {
        // Must run synchronously, before the first await below - some Android
        // Chrome builds only treat speechSynthesis.speak() as allowed when it
        // happens inside the actual user-gesture callstack. Once unlocked this
        // way, later speak() calls from async contexts (the ZXing decode
        // callback has no gesture of its own at all) keep working for the
        // rest of the page session. Without this, voice feedback worked for
        // the hardware scanner (real KeyboardEvents keep their own gesture
        // standing) but silently never fired for camera-mode scans.
        this.primeSpeech();
        // Checks the ALREADY-DECIDED permission state before ever attempting
        // getUserMedia, so a camera that's permanently blocked on this device
        // (see cameraErrorMessage's NotAllowedError case - once denied, the
        // browser never shows its native prompt again) shows the "here's how
        // to re-enable it" guidance immediately instead of after a failed
        // open attempt. Not supported on every browser (notably older iOS
        // Safari doesn't recognize 'camera' as a queryable permission name) -
        // wrapped in try/catch so it just falls through to the normal
        // attempt-and-see path there, exactly like before this existed.
        try {
            if (navigator.permissions && navigator.permissions.query) {
                var status = await navigator.permissions.query({ name: 'camera' });
                if (status.state === 'denied') {
                    this.setState({ cameraOpen: true, cameraStreaming: false, cameraError: cameraErrorMessage({ name: 'NotAllowedError' }) });
                    return;
                }
            }
        } catch (e) { /* Permissions API not supported for 'camera' here - fall through */ }
        this.setState({ cameraOpen: true, cameraStreaming: false, cameraError: '' });
    };

    stopCameraScan = () => {
        if (this.scanControls) {
            this.scanControls.stop();
            this.scanControls = null;
        }
        this.restoreConsoleWarn();
    };

    // ZXing's MultiFormatReader logs console.warn('MultiFormatReader:
    // non-ReaderException from reader:', ex) internally whenever a frame's
    // decode failure isn't recognized as its own expected
    // "nothing found here" exception type - in this build that's firing for
    // every single video frame with no barcode in view (i.e. continuously,
    // the entire time the operator is still aiming), not just genuine
    // unexpected errors. There's no reader option to turn this off - it's an
    // unconditional call inside the library itself - so this filters out only
    // that exact known-benign message for as long as the camera is actually
    // open, restoring the real console.warn the moment it closes. Any other
    // warning (from this app or anything else) still goes through untouched.
    silenceZxingConsoleSpam = () => {
        if (this._originalConsoleWarn) return; // already silenced
        this._originalConsoleWarn = console.warn.bind(console);
        var original = this._originalConsoleWarn;
        console.warn = function (msg) {
            if (typeof msg === 'string' && msg.indexOf('MultiFormatReader: non-ReaderException') === 0) return;
            original.apply(console, arguments);
        };
    };
    restoreConsoleWarn = () => {
        if (this._originalConsoleWarn) {
            console.warn = this._originalConsoleWarn;
            this._originalConsoleWarn = null;
        }
    };

    closeCamera = () => {
        this.stopCameraScan();
        this.setState({ cameraOpen: false, cameraStreaming: false });
        this.focusBarcodeInput();
    };

    startCameraScan = async () => {
        // Checked explicitly up front rather than only inferred from whatever
        // error the browser happens to throw below - browsers are inconsistent
        // here: some throw a clearly-named SecurityError for an insecure
        // origin, others just never define navigator.mediaDevices at all (a
        // plain, unhelpfully-named TypeError). This is also the ONLY situation
        // that should ever mention HTTPS - on an already-HTTPS production site
        // this is always true, so every real failure falls through to the
        // catch below and gets mapped to what it actually was instead.
        if (!window.isSecureContext) {
            this.setState({ cameraStreaming: false, cameraError: 'Camera access needs HTTPS (or localhost) - this page is not loaded over a secure connection.' });
            return;
        }
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            this.setState({ cameraStreaming: false, cameraError: 'Camera scanning is not supported on this browser/device.' });
            return;
        }
        try {
            // Always a fresh reader, not reused across opens - a cached
            // instance from before a hints change (e.g. a hot-reloaded dev
            // session that never did a full page refresh) would otherwise
            // keep decoding with whatever POSSIBLE_FORMATS it was originally
            // constructed with, silently ignoring BARCODE_SCAN_HINTS' current
            // value. Cheap to recreate - this only runs once per camera open.
            this.zxingReader = new BrowserMultiFormatReader(BARCODE_SCAN_HINTS);
            this.cameraScanCooldownUntil = 0;
            this.silenceZxingConsoleSpam();
            // undefined deviceId - zxing/getUserMedia picks a sensible default
            // (rear camera on a phone), which is what a warehouse scan needs.
            this.scanControls = await this.zxingReader.decodeFromVideoDevice(undefined, this.videoRef.current, (result, err, controls) => {
                // err fires on every frame with no decodable barcode in it - that's
                // normal while aiming the camera, not a real failure; only a
                // successful result is ever actionable here.
                if (!result) return;
                var now = Date.now();
                // Still cooling down from the last accepted decode - the same
                // carton is almost certainly still sitting in frame, not a second
                // deliberate scan.
                if (now < this.cameraScanCooldownUntil) return;
                this.cameraScanCooldownUntil = now + CAMERA_SCAN_COOLDOWN_MS;
                // Camera stays open across scans now - one "Scan with Camera" tap
                // covers the whole trip instead of needing a re-tap after every
                // carton. It closes itself once nothing's left (componentDidUpdate)
                // or via the explicit Close button; controls/scanControls are left
                // running either way, unaffected by this callback.
                this.submitBarcode('camera', result.getText());
            });
            // decodeFromVideoDevice only resolves after getUserMedia succeeded
            // and the stream is attached to the <video>, so the operator may
            // have tapped Close while it was still pending. closeCamera's
            // stopCameraScan ran before scanControls existed, so stop the
            // just-started stream here instead of leaving the camera running
            // (and its LED on) behind a closed overlay.
            if (!this.state.cameraOpen) {
                this.stopCameraScan();
                return;
            }
            this.setState({ cameraStreaming: true });
        } catch (e) {
            console.log('Camera scan failed to start', e);
            this.setState({ cameraStreaming: false, cameraError: cameraErrorMessage(e) });
        }
    };

    // feedbackType names which SCAN_FEEDBACK entry this alert's voice/vibration
    // actually is - defaults to cls, which is correct for duplicate/wrong/
    // notfound (their cls IS their feedback key), but offline/networkError
    // both reuse cls:'wrong' purely for the modal's icon/color (there's no
    // dedicated icon for them) while using their own distinct, more accurate
    // speech - those callers pass feedbackType explicitly. Stored on the alert
    // itself so maybeRepeatPendingAck can replay the SAME voice later without
    // needing to know why this particular alert was raised.
    showAlert = (title, sub, cls, needsAck, feedbackType) => {
        clearTimeout(this.alertTimer);
        this.setState({ alert: { show: true, title, sub, cls, needsAck: !!needsAck, feedbackType: feedbackType || cls } });
        // No auto-dismiss timer for an alert that needs acknowledgement - it only
        // ever closes via acknowledgeAlert (the operator's own OK tap).
        if (needsAck) return;
        this.alertTimer = setTimeout(() => {
            this.setState((prev) => ({ alert: { ...prev.alert, show: false } }));
        }, 3000);
    };

    // Central "there's already something pending" guard - a new scan attempt,
    // or a background event like the connection dropping, that shows up while
    // a needsAck alert is already up must never stack a second alert or
    // silently replace it with a different one. Instead it just re-voices the
    // SAME alert that's already waiting on the operator (throttled so rapid
    // triggers - a scanner fired repeatedly, or several concurrent write
    // failures - don't stack overlapping speech). Returns true if it consumed
    // the trigger this way (caller should stop, nothing else to do); false if
    // there's genuinely nothing pending and the caller is clear to raise one.
    maybeRepeatPendingAck = () => {
        if (!(this.state.alert.show && this.state.alert.needsAck)) return false;
        var now = Date.now();
        if (!this.lastAckReminderAt || now - this.lastAckReminderAt > 2500) {
            this.lastAckReminderAt = now;
            this.playScanFeedback('ackReminder');
        }
        return true;
    };

    // The only way a needsAck alert closes - explicit, deliberate, and required
    // before submitBarcode will accept another scan attempt (see its own guard).
    // Also clears networkError - by design (see that state field's own comment)
    // this doesn't wait for a background check to confirm the connection is
    // really back; the very next scan attempt itself is the retry, and it will
    // set networkError again immediately if the API still isn't reachable.
    acknowledgeAlert = () => {
        this.setState((prev) => ({ alert: { ...prev.alert, show: false, needsAck: false }, networkError: false }));
        this.focusBarcodeInput();
    };

    // The offline popup's own button (see the acknowledgeModal render below,
    // which picks this over acknowledgeAlert specifically for feedbackType
    // 'offline') - deliberately not a plain dismiss. The primary path back is
    // still automatic (onGoOnline, fired by the browser's real 'online' event),
    // but that event isn't perfectly reliable on every Android WebView this app
    // runs on - if it's ever missed or delayed, a bare OK would let the operator
    // dismiss the warning on faith while still genuinely offline. This re-checks
    // navigator.onLine at the exact moment of the tap instead: onGoOnline (same
    // auto-dismiss logic as the real event) only runs if the device actually
    // reports online right now; otherwise it stays up and just confirms over
    // voice that it's still checking.
    retryConnection = () => {
        if (typeof navigator !== 'undefined' && navigator.onLine) {
            this.onGoOnline();
        } else {
            this.playScanFeedback('offline');
        }
    };

    // Best-effort - vibration/Web Audio/speech aren't available or allowed
    // everywhere (desktop browsers with no vibration motor, iOS Safari before
    // any user gesture has unlocked audio, etc.) - never let missing/blocked
    // feedback interrupt the actual scan flow.
    playScanFeedback = (type, isFinalScan) => {
        var feedback = SCAN_FEEDBACK[type];
        if (!feedback) return;
        try {
            if (navigator.vibrate) navigator.vibrate(feedback.vibrate);
        } catch (e) { /* ignore */ }
        var beepPlayed = false;
        try {
            var AudioCtx = window.AudioContext || window.webkitAudioContext;
            if (AudioCtx) {
                if (!MobileScan._audioCtx) MobileScan._audioCtx = new AudioCtx();
                var audioCtx = MobileScan._audioCtx;
                var osc = audioCtx.createOscillator();
                var gain = audioCtx.createGain();
                osc.type = 'sine';
                osc.frequency.value = feedback.freq;
                gain.gain.value = 0.15;
                osc.connect(gain);
                gain.connect(audioCtx.destination);
                osc.start();
                osc.stop(audioCtx.currentTime + feedback.duration);
                beepPlayed = true;
            }
        } catch (e) { /* ignore */ }
        // Sequenced to start after the beep finishes, not layered on top of it -
        // a tone and a spoken word at the exact same instant are harder to tell
        // apart than one right after the other, especially over noisy warehouse
        // floor equipment. Only actually delayed when the beep really played;
        // if Web Audio wasn't available at all there's nothing to wait out.
        // The very last carton gets a follow-up announcement chained after its
        // own outcome word via speakSequence's onend queueing, so it waits for
        // "Scanned" to actually finish instead of cutting it off (a second
        // separate speakSequence call would cancel the first mid-utterance).
        // Wording matches the visual "All Cartons Loaded" toast shown at the same
        // moment (see showOutcomeAlert) - one consistent message, not two different
        // ones depending on whether the operator is looking at the screen.
        var lang = this.context.voiceLang;
        var feedbackSpeech = feedback.speech[lang] || feedback.speech.en;
        var speechTexts = isFinalScan ? [feedbackSpeech, ALL_CARTONS_LOADED_SPEECH[lang] || ALL_CARTONS_LOADED_SPEECH.en] : [feedbackSpeech];
        clearTimeout(this.speakTimer);
        if (beepPlayed) {
            this.speakTimer = setTimeout(() => this.speakSequence(speechTexts), feedback.duration * 1000);
        } else {
            this.speakSequence(speechTexts);
        }
    };

    // Vibrate+beep only (see PROCESSING_FEEDBACK) - deliberately no speech, since
    // this fires on every single 'valid' scan while its server confirmation is
    // in flight and a spoken word on every scan (on top of the real outcome
    // word moments later) would be constant noise instead of useful signal.
    // Reuses playScanFeedback's own oscillator instance (MobileScan._audioCtx)
    // rather than duplicating the setup, but skips speakSequence entirely.
    playProcessingChirp = () => {
        try {
            if (navigator.vibrate) navigator.vibrate(PROCESSING_FEEDBACK.vibrate);
        } catch (e) { /* ignore */ }
        try {
            var AudioCtx = window.AudioContext || window.webkitAudioContext;
            if (AudioCtx) {
                if (!MobileScan._audioCtx) MobileScan._audioCtx = new AudioCtx();
                var audioCtx = MobileScan._audioCtx;
                var osc = audioCtx.createOscillator();
                var gain = audioCtx.createGain();
                osc.type = 'sine';
                osc.frequency.value = PROCESSING_FEEDBACK.freq;
                gain.gain.value = 0.1;
                osc.connect(gain);
                gain.connect(audioCtx.destination);
                osc.start();
                osc.stop(audioCtx.currentTime + PROCESSING_FEEDBACK.duration);
            }
        } catch (e) { /* ignore */ }
    };

    // Web Speech API - built into every modern mobile browser (Chrome/Safari on
    // Android/iOS), no package needed. The one feedback channel that still gets
    // through when the operator isn't looking at the screen AND the vibration/
    // beep can't cut through noisy warehouse floor equipment. Cancels whatever's
    // still being read before starting the next one, so a burst of consecutive
    // scans doesn't queue up speech that lags behind what's actually happening.
    // texts speak in order, each one only starting once the previous has
    // actually finished (chained via onend) rather than all firing at once -
    // needed for the last-carton "Scanned" + "Loading complete" pair, and works
    // just as well for the single-word case every other scan uses.
    speakSequence = (texts, lang) => {
        try {
            if (!('speechSynthesis' in window)) return;
            window.speechSynthesis.cancel();
            var resolvedLang = lang || this.context.voiceLang;
            var queue = (texts || []).filter(Boolean);
            var speakNext = () => {
                var text = queue.shift();
                if (!text) return;
                var utterance = new SpeechSynthesisUtterance(text);
                utterance.rate = 0.85;
                utterance.volume = 1;
                utterance.lang = resolvedLang === 'hi' ? 'hi-IN' : 'en-IN';
                utterance.onend = speakNext;
                utterance.onerror = speakNext;
                window.speechSynthesis.speak(utterance);
            };
            speakNext();
        } catch (e) { /* ignore */ }
    };

    // Silent (volume 0) throwaway utterance, fired once on mount purely to pay
    // the TTS engine's cold-start cost early - see the componentDidMount comment.
    // A genuinely empty string gets ignored by some browsers without ever
    // touching the engine at all, so this uses a single space instead.
    primeSpeech = () => {
        try {
            if (!('speechSynthesis' in window)) return;
            var utterance = new SpeechSynthesisUtterance(' ');
            utterance.volume = 0;
            window.speechSynthesis.speak(utterance);
        } catch (e) { /* ignore */ }
    };

    // Every exception outcome requires acknowledgement (needsAck:true, the last
    // showAlert argument below) - only a genuine 'valid' scan auto-dismisses, so
    // a clean run through a trip stays fast while an exception can't be scanned
    // straight through unnoticed.
    showOutcomeAlert = (result, remainingBefore) => {
        var ctx = this.context;
        this.playScanFeedback(result.type, result.type === 'valid' && remainingBefore === 1);
        if (result.type === 'duplicate') {
            this.showAlert('DUPLICATE — carton already scanned', `${result.code} was scanned earlier. Please scan a new carton.`, 'duplicate', true);
        } else if (result.type === 'valid') {
            this.showAlert('Carton scanned successfully', result.hu.id, 'valid');
            if (remainingBefore === 1) {
                // Overwrites the toast above, matching test.html's same-box behavior
                // when the last expected carton is scanned.
                this.showAlert('All Cartons Loaded', 'Tap Finish Loading to complete this trip.', 'valid');
            }
        } else if (result.type === 'wrong') {
            this.showAlert('WRONG TRUCK — carton for a different trip', `This HU belongs to Trip ${result.tripNo}, not Trip ${ctx.session.loadVehicle}.`, 'wrong', true);
        } else if (result.type === 'notfound') {
            this.showAlert('NOT FOUND — carton not in system', 'This barcode could not be matched to any HU in this plan.', 'notfound', true);
        }
    };

    // Posts the audit-trail write and returns the server's response - callers
    // decide what (if anything) to do with it. See
    // MobileTruckLoadContext.recordScanReal for why this is no longer a true
    // fire-and-forget, and submitBarcode for how the response actually gets
    // used: only a 'valid' result ever needs to look at it (see there for why).
    logScan = (result, isAuto) => {
        var ctx = this.context;
        var s = ctx.session;
        return ctx.recordScanReal({
            transaction_id: s.planId,
            plan_id: s.planId,
            loading_port_code: ctx.portCode,
            trip_no: s.loadVehicle,
            vehicle_number: s.vehicleNumber,
            lsp: s.lsp,
            dock_number: s.dockNumber,
            scanner_name: s.scannerName,
            hu: result.code,
            outcome: result.type,
            matched_trip_no: result.tripNo || null,
            consignee: result.hu ? result.hu.consignee : null,
            is_auto: !!isAuto,
            plan_date: s.loadDate,
            scan_attempt_id: generateScanAttemptId()
        });
    };

    // codeOverride is used by the camera and global-scan-capture paths - neither
    // one passed the code through the barcode input, so there's nothing in state
    // to read. forcedIsAuto lets a caller that already worked out its own
    // keystroke-timing (the global capture path) skip this method's own
    // source-based inference entirely, instead of needing to fake a source string
    // that would trigger the same logic indirectly.
    submitBarcode = (source, codeOverride, forcedIsAuto) => {
        var ctx = this.context;
        if (ctx.session.remainingHUs.length === 0 || this.state.scanPhase === 'processing') return;
        // An exception alert is still up, unacknowledged - refuse every further
        // scan attempt (any source: manual, scanner, camera, global capture -
        // they all funnel through here) until the operator explicitly taps OK
        // (see acknowledgeAlert). This is the actual fix for an operator scanning
        // straight through a duplicate/wrong-truck alert they didn't register and
        // ending up with the same carton scanned several times in a row.
        if (this.maybeRepeatPendingAck()) {
            // Plays the distinct 'ackReminder' phrase, not the pending alert's
            // own voice - see that feedback entry's own comment for why
            // replaying e.g. "Duplicate" here would sound like a verdict on
            // this new, never-actually-evaluated attempt.
            this.setState({ barcodeValue: '' });
            return;
        }
        var code = (codeOverride !== undefined ? codeOverride : this.state.barcodeValue).trim();
        if (!code) return;
        // Blocked here, not just visually disabled - a real scanner still fires
        // its own onChange regardless of the input's disabled attribute. Without
        // this, processScanOutcome would still mark the HU loaded locally while
        // offline, with nothing able to reach lm_truckload_scan_log to back it
        // up - exactly the gap that let an operator walk through an entire
        // "successful" trip offline only to have it come back empty after a
        // refresh. No queue/retry behind this - the attempt is simply refused,
        // so the operator just scans it again once back online.
        //
        // Routed through raiseOfflineAlert (not its own inline showAlert call,
        // which used to live here) - that used to skip the feedbackType:'offline'
        // tag, so a repeat attempt against THIS alert replayed feedbackType's
        // default (cls, 'wrong') via maybeRepeatPendingAck - "Wrong truck" for a
        // plain offline block, with no actual wrong-truck scan involved. One
        // single place now raises this alert, so it can't drift out of sync again.
        if (this.state.isOffline) {
            this.setState({ barcodeValue: '' });
            this.raiseOfflineAlert();
            return;
        }
        // A camera decode is always an automated read, same category as a
        // handheld scanner - never manual typing, so it skips the keystroke-timing
        // check entirely rather than needing to fake a firstKeyTime for it.
        var isAuto;
        if (forcedIsAuto !== undefined) {
            isAuto = forcedIsAuto;
        } else if (source === 'camera') {
            isAuto = true;
        } else {
            var elapsed = this.firstKeyTime !== null ? (Date.now() - this.firstKeyTime) : Infinity;
            isAuto = source === 'keyboard' && elapsed <= SCANNER_SPEED_THRESHOLD_MS;
        }
        this.firstKeyTime = null;
        var remainingBefore = ctx.session.remainingHUs.length;
        this.setState({ scanPhase: 'processing', barcodeValue: '' });
        // processScanOutcome itself is a fast local lookup (no network round-trip
        // blocks it), so this only needs a tiny buffer against a genuinely
        // double-fired scan landing while scanPhase is still flipping back to
        // 'idle', not the 400ms this used to be - that was pure unexplained
        // wait, not anything actually being computed.
        setTimeout(async () => {
            var result = ctx.processScanOutcome(code, isAuto);
            // scanPhase resets right away regardless of outcome/what happens
            // below - the operator can keep scanning the next carton while a
            // 'valid' result's own confirmation (see below) is still in
            // flight, same responsiveness as before this change.
            this.setState({ scanPhase: 'idle' });
            // duplicate/wrong/notfound are fully determined by data this
            // client already has in full (its own scannedValidIds/
            // allPlanHUs) - nothing the server says back can ever change
            // them, so show them immediately, same as always. The write is
            // still checked for failure afterward (not awaited before showing
            // the outcome - these never touch loaded/remainingHUs, so there's
            // nothing to roll back) so a dead connection gets caught here too,
            // not only on a 'valid' scan.
            if (result.type !== 'valid') {
                this.showOutcomeAlert(result, remainingBefore);
                this.logScan(result, isAuto).then((response) => {
                    if (!response.ok) this.handleScanWriteFailed();
                });
                return;
            }
            // 'valid' is the ONE outcome that depends on what another device
            // might be doing at this same instant (see claimPlanDataHuScanned)
            // - so unlike every other outcome, it waits for the server's
            // answer before telling the operator anything at all, instead of
            // showing "scanned successfully" and then, moments later,
            // reversing itself. The wait is invisible to the rest of the
            // session state - processScanOutcome already updated
            // remainingHUs/loaded/the progress bar above, synchronously, so
            // nothing about the trip's own tracking is delayed, only this
            // one alert. playProcessingChirp fills that gap for an operator
            // who isn't looking at the screen - a quick, distinct tick so the
            // scan doesn't feel like it silently did nothing until the real
            // outcome sound plays a moment later.
            this.playProcessingChirp();
            var response = await this.logScan(result, isAuto);
            if (!response.ok) {
                // The write never reached the server at all (see recordScanReal) -
                // this is the actual fix for "scanned all HUs but nothing shows":
                // previously a network failure here fell through to the success
                // branch below (a falsy response looked exactly like "no race
                // conflict"), so the operator was told "scanned successfully" for
                // a carton lm_truckload_scan_log never received. Roll the local
                // optimistic credit back (NOT reconcileRaceLoss - this was never
                // a duplicate, just never recorded) and block further scanning
                // the same way an offline device already does.
                ctx.reconcileScanNotSaved(result.code, result.hu, isAuto);
                this.handleScanWriteFailed();
                return;
            }
            if (response.data && response.data.outcome && response.data.outcome !== 'valid') {
                ctx.reconcileRaceLoss(result.code, result.hu, isAuto);
                this.showAlert(
                    'CARTON REASSIGNED — already loaded by another scanner',
                    `${result.code} was just completed on this trip by a different device. Please recheck this carton.`,
                    'duplicate',
                    true
                );
            } else if (!(this.state.alert.show && this.state.alert.needsAck)) {
                // Two scans can genuinely overlap in flight (scanPhase resets to
                // 'idle' right after the local classification above, well before
                // this await resolves - see the comment on that reset) - if an
                // earlier scan's failure already put up a needsAck "check your
                // network" alert, a THIS scan's own late-arriving success must
                // never silently overwrite it with a plain "scanned successfully"
                // toast. The count already updated correctly either way
                // (processScanOutcome ran synchronously above); only the visible
                // alert is skipped here, so the more important one stays on
                // screen until the operator actually acknowledges it.
                this.showOutcomeAlert(result, remainingBefore);
            }
        }, SCAN_PROCESS_DELAY_MS);
    };

    // Shared by both the awaited ('valid') and fire-and-forget (duplicate/
    // wrong/notfound) write-failure paths above - one consistent "stop and
    // check your connection" acknowledgement and blocked state regardless of
    // which kind of scan the failed write was for. Guarded on networkError
    // already being set - two scans failing close together (same overlap as
    // above) would otherwise both call this, showing the identical alert twice
    // and, worse, vibrating/beeping/speaking "Network error" twice back to
    // back for what the operator experiences as one problem.
    handleScanWriteFailed = () => {
        if (this.state.networkError) return;
        this.setState({ networkError: true });
        this.playScanFeedback('networkError');
        // feedbackType explicit here - cls stays 'wrong' (no dedicated icon for
        // this), but the voice a repeated attempt replays (maybeRepeatPendingAck)
        // must stay 'networkError', not fall back to cls and say "Wrong truck".
        this.showAlert(
            'Check Your Network Connection',
            'This scan could not be saved - please check your connection and scan this carton again.',
            'wrong',
            true,
            'networkError'
        );
    };

    onBarcodeKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            clearTimeout(this.autoSubmitTimer);
            this.submitBarcode('keyboard');
            return;
        }
        if (this.firstKeyTime === null) {
            this.firstKeyTime = Date.now();
        }
        this.armAutoSubmit();
    };

    // See AUTO_SUBMIT_PAUSE_MS - covers a scanner with no Enter/Tab suffix
    // configured. Restarted on every keystroke; only actually submits once
    // there's been silence for that long right after fast (scanner-speed) entry.
    armAutoSubmit = () => {
        clearTimeout(this.autoSubmitTimer);
        this.autoSubmitTimer = setTimeout(() => {
            var elapsed = this.firstKeyTime !== null ? (Date.now() - this.firstKeyTime) : Infinity;
            if (this.state.barcodeValue.length >= MIN_AUTO_SUBMIT_LENGTH && elapsed <= SCANNER_SPEED_THRESHOLD_MS) {
                this.submitBarcode('keyboard');
            }
        }, AUTO_SUBMIT_PAUSE_MS);
    };

    // Scan mode's visible input (see render) only ever receives scanner-injected
    // text - Manual Entry is the one place a human actually types - so it doesn't
    // need armAutoSubmit's elapsed<=SCANNER_SPEED_THRESHOLD_MS "was this really a
    // scanner and not a person typing" check at all. That check is exactly what
    // was silently swallowing every scan here: this device's scanner integration
    // injects text through Android's IME layer, which can take well over 300ms to
    // land a whole barcode, so the elapsed-since-first-keystroke gate kept failing
    // and the code just sat in the box with no button to submit it manually
    // either. Here, any pause after the last keystroke means the scan is done -
    // no speed check needed since there's no human-typing case to rule out.
    onScanInputChange = (e) => {
        var value = e.target.value;
        this.setState({ barcodeValue: value });
        clearTimeout(this.scanAutoSubmitTimer);
        if (!value) return;
        this.scanAutoSubmitTimer = setTimeout(() => {
            this.submitBarcode('scanner', value, true);
        }, SCAN_INPUT_AUTO_SUBMIT_MS);
    };

    // Still handled in case this scanner's configured WITH an Enter/Tab
    // terminator after all - fires immediately instead of waiting out the pause.
    onScanInputKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            clearTimeout(this.scanAutoSubmitTimer);
            this.submitBarcode('scanner', this.state.barcodeValue, true);
        }
    };

    // Reads the real, server-side aggregation (lm_truckload_plan_data joined with
    // lm_truckload_scan_log - see MobileTruckLoadContext.fetchConsigneeSummary)
    // instead of the local in-memory session counters, so Summary reflects what's
    // actually been recorded rather than just this browser tab's own tally. Falls
    // back to the local computation if the fetch fails, so a network hiccup can't
    // block viewing a summary.
    viewSummary = () => {
        if (this.state.viewingSummary) return;
        var ctx = this.context;
        var s = ctx.session;
        this.setState({ viewingSummary: true });
        var pushSummary = (real) => {
            this.props.history.push({
                // planId/loadVehicle in the path (not just router state) means a
                // real refresh on Summary can rebuild itself via resumeSessionFromIds
                // instead of losing everything - see that method's own comment.
                pathname: `/lm-mobile/summary/${s.planId}/${s.loadVehicle}`,
                state: {
                    mode: 'live',
                    breadcrumbLabel: 'Summary',
                    vehicle: s.vehicleNumber || s.loadVehicle,
                    date: ctx.formatDate(s.loadDate),
                    planId: s.planId,
                    loadType: s.loadType,
                    tripNo: s.loadVehicle,
                    lsp: s.lsp,
                    dockNumber: s.dockNumber,
                    scannerName: s.scannerName,
                    expectedTotal: real ? real.expected : s.expectedHUs.length,
                    scannedTotal: real ? real.loaded : s.loaded,
                    remainingTotal: real ? real.remaining : s.remainingHUs.length,
                    autoScans: real ? real.autoScanCount : s.autoScanCount,
                    manualScans: real ? real.manualScanCount : s.manualScanCount,
                    duplicateTotal: real ? real.duplicateCount : s.duplicateCount,
                    wrongTruckTotal: real ? real.wrongTruckCount : s.wrongTruckCount,
                    notFoundTotal: real ? real.notFoundCount : s.notFoundCount,
                    consigneeRows: real ? real.consignees : ctx.computeConsigneeMetrics(s.expectedHUs, s.remainingHUs, s.manualByConsignee, s.autoByConsignee),
                    returnScreen: 'scan'
                }
            });
        };
        ctx.fetchConsigneeSummary(s.planId, s.loadVehicle).then(pushSummary).catch((e) => {
            console.log('fetchConsigneeSummary failed, falling back to local counts', e);
            pushSummary(null);
        });
    };

    render() {
        var ctx = this.context;
        var s = ctx.session;
        var expected = s.expectedHUs.length;
        var remaining = s.remainingHUs.length;
        var allScanned = remaining === 0;
        // Clamped defensively - loaded should never exceed expected now that
        // resumeSession skips orphaned historical scans (see
        // MobileTruckLoadContext), but a progress bar showing "106%" would
        // look broken regardless of the exact cause, so this stays capped
        // even if some other future edge case produces loaded > expected.
        var pct = expected > 0 ? Math.min(100, Math.round((s.loaded / expected) * 100)) : 0;
        var uniqueOrders = ctx.distinct(s.remainingHUs.map(hu => hu.sub)).length;
        var alert = this.state.alert;
        var ackPending = alert.show && alert.needsAck;
        // networkError is always set together with an ackPending alert (see
        // submitBarcode/logScan below), so this is belt-and-braces rather than
        // load-bearing - kept explicit so scanning can never resume mid-render
        // between the two flipping if that ever changes.
        var scanBlocked = allScanned || this.state.isOffline || this.state.networkError || ackPending;

        return (
            <>
                <div className="mtl-screen-scroll">
                        <div className="mtl-breadcrumb" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                            <span><span className="mtl-crumb-parent">Truck Load Automation</span><span className="mtl-crumb-sep">&rsaquo;</span><span className="mtl-crumb-parent">Input Plan</span><span className="mtl-crumb-sep">&rsaquo;</span><b>Scan</b></span>
                            {/* Also reachable from the hamburger menu (MobileHeader) via the
                                same MobileTruckLoadContext.voiceLang/setVoiceLang - duplicated
                                here too since the hamburger menu itself doesn't show on this
                                screen (Scan uses a back-arrow, not mode:'menu' - see
                                MobileHeader's SCREEN_META), so that copy alone isn't reachable
                                from the one screen that actually uses the voice feedback. */}
                            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 600, color: '#546e7a', whiteSpace: 'nowrap' }}>
                                🔊
                                <select
                                    value={this.context.voiceLang}
                                    onChange={(e) => this.context.setVoiceLang(e.target.value)}
                                    style={{ padding: '2px 6px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '12px', fontWeight: 600, background: '#fff', color: '#333' }}
                                >
                                    <option value="en">English</option>
                                    <option value="hi">हिंदी</option>
                                </select>
                            </label>
                        </div>
                        {/* Persistent, not a fading alert - being offline is an ongoing
                            condition the operator needs to see the whole time, not a
                            one-off event. Scanning is actually blocked in submitBarcode;
                            this is just the visible reason why. No retry/sync queue behind
                            this - a scan attempted while offline is simply refused, not
                            silently accepted, so once the connection is back the operator
                            just scans it again. */}
                        {this.state.isOffline && (
                            <div className="mtl-error" style={{ display: 'block', margin: '10px 14px 0', background: '#ffcdd2', color: '#b71c1c', fontWeight: 600 }}>
                                No data connection — scanning is paused. Please try again once you're back online.
                            </div>
                        )}
                        {/* needsAck alerts (exceptions - see showAlert) no longer use this
                            fading top toast at all - they get the full-screen, compulsory
                            acknowledgeModal below instead, so this banner only ever shows a
                            plain scan-success toast that fades on its own. */}
                        <div className={"mtl-alert-box" + ((alert.show && !alert.needsAck) ? " mtl-show mtl-" + alert.cls : "")}>
                            <span className="mtl-icon">{ALERT_ICONS[alert.cls] || '•'}</span>
                            <span className="mtl-txt">{alert.title}{alert.sub ? <small>{alert.sub}</small> : null}</span>
                        </div>

                        {/* mtl-summary-details raises the accordion-body max-height cap
                            (see CSS) so this fixed-length field list never triggers its
                            own internal scrollbar - unlike Missing HUs below, which
                            genuinely can grow long and should stay scroll-capped. */}
                        <div className={"mtl-accordion mtl-summary-details" + (this.state.detailsOpen ? " mtl-open" : "")}>
                            <button className="mtl-accordion-header" type="button" onClick={() => this.setState((p) => ({ detailsOpen: !p.detailsOpen }))}>
                                <span className="mtl-detail-section-icon mtl-icon-blue">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="7" width="14" height="10" rx="1"></rect><path d="M15 10h4l3 3v4h-7z"></path><circle cx="6" cy="18.5" r="1.7"></circle><circle cx="17.5" cy="18.5" r="1.7"></circle></svg>
                                </span>
                                <span className="mtl-accordion-title">Load Details</span>
                                <svg className="mtl-accordion-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
                            </button>
                            <div className="mtl-accordion-body">
                                <div className="mtl-detail-kv">
                                    <div className="mtl-detail-kv-row"><span className="mtl-detail-kv-label">Trip Type</span><span className="mtl-detail-kv-value">{s.loadType || '—'}</span></div>
                                    <div className="mtl-detail-kv-row"><span className="mtl-detail-kv-label">Trip No</span><span className="mtl-detail-kv-value">{s.loadVehicle || '—'}</span></div>
                                    <div className="mtl-detail-kv-row"><span className="mtl-detail-kv-label">LSP</span><span className="mtl-detail-kv-value">{s.lsp || '—'}</span></div>
                                    <div className="mtl-detail-kv-row"><span className="mtl-detail-kv-label">Actual Vehicle No</span><span className="mtl-detail-kv-value">{s.vehicleNumber || '—'}</span></div>
                                    <div className="mtl-detail-kv-row"><span className="mtl-detail-kv-label">Dock Number</span><span className="mtl-detail-kv-value">{s.dockNumber || '—'}</span></div>
                                    <div className="mtl-detail-kv-row"><span className="mtl-detail-kv-label">Scanner Name</span><span className="mtl-detail-kv-value">{s.scannerName || '—'}</span></div>
                                    <div className="mtl-detail-kv-row mtl-last"><span className="mtl-detail-kv-label">Load Date</span><span className="mtl-detail-kv-value">{ctx.formatDate(s.loadDate) || '—'}</span></div>
                                </div>
                            </div>
                        </div>

                        {/* Scan mode's input is hidden, not absent - see the entryMode state
                            comment for why it still needs real focus. Manual Entry is the one
                            place typing (and its keyboard) is actually wanted. Scan with Camera
                            works from either mode - it's an orthogonal input source. */}
                        <div className="mtl-status-tabs" style={{ margin: '0 14px 10px' }}>
                            <button type="button" className={"mtl-status-tab" + (this.state.entryMode === 'scan' ? " mtl-active" : "")} onClick={() => this.setEntryMode('scan')}>Scan</button>
                            <button type="button" className={"mtl-status-tab" + (this.state.entryMode === 'manual' ? " mtl-active" : "")} onClick={() => this.setEntryMode('manual')}>Manual Entry</button>
                        </div>

                        <div className="mtl-scan-wrap">
                            {this.state.entryMode === 'manual' ? (
                                <div className="mtl-barcode-input-row">
                                    <span className="mtl-barcode-row-icon">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="1"></rect><line x1="7" y1="7" x2="7" y2="17"></line><line x1="11" y1="7" x2="11" y2="17"></line><line x1="15" y1="7" x2="15" y2="17"></line><line x1="18" y1="7" x2="18" y2="17"></line></svg>
                                    </span>
                                    <input
                                        // disabled only for allScanned (genuinely nothing left to do) -
                                        // NOT for the rest of scanBlocked (offline/networkError/
                                        // ackPending). A real disabled attribute blocks focus and
                                        // keystrokes outright, which was silently swallowing every
                                        // scan attempt instead of letting submitBarcode's own guards
                                        // catch it and re-warn the operator - see the SCSS comment on
                                        // mtl-barcode-input-blocked for the full story.
                                        className={"mtl-barcode-input" + (scanBlocked && !allScanned ? " mtl-barcode-input-blocked" : "")}
                                        type="text"
                                        placeholder="Type barcode"
                                        autoComplete="off"
                                        ref={this.barcodeRef}
                                        disabled={allScanned}
                                        value={this.state.barcodeValue}
                                        onChange={(e) => this.setState({ barcodeValue: e.target.value })}
                                        onKeyDown={this.onBarcodeKeyDown}
                                    />
                                    <button className="mtl-barcode-submit-btn" type="button" disabled={scanBlocked} onClick={() => this.submitBarcode('button')}>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
                                        <span className="barcode-submit-label">Enter</span>
                                    </button>
                                </div>
                            ) : (
                                <>
                                    {/* Confirmed: this scanner injects text via IME commit, not real
                                        per-character keydowns - a document-level listener sees only
                                        generic key:"Unidentified" noise with no actual character data
                                        in it, focused or not. A real, visible, fully editable, focused
                                        input is the only place the decoded value itself ever shows up
                                        (its own input/onChange event's e.target.value). Off-screen
                                        positioning, inputMode="none", and readOnly-until-focused were
                                        all tried and ruled out for hiding it - see focusBarcodeInput -
                                        each one blocks the same InputConnection the scanner injects
                                        through, not just the visible keyboard. */}
                                    <div className="mtl-barcode-input-row" style={{ margin: '0 0 12px' }}>
                                        <span className="mtl-barcode-row-icon">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="1"></rect><line x1="7" y1="7" x2="7" y2="17"></line><line x1="11" y1="7" x2="11" y2="17"></line><line x1="15" y1="7" x2="15" y2="17"></line><line x1="18" y1="7" x2="18" y2="17"></line></svg>
                                        </span>
                                        <input
                                            // Same reasoning as the Manual Entry input above - disabled
                                            // only for allScanned, a visual-only blocked class for
                                            // everything else, so a scanner injecting keystrokes while
                                            // offline/blocked still lands here and hits submitBarcode's
                                            // own guards instead of going nowhere.
                                            className={"mtl-barcode-input" + (scanBlocked && !allScanned ? " mtl-barcode-input-blocked" : "")}
                                            type="text"
                                            placeholder="Waiting for scan..."
                                            autoComplete="off"
                                            ref={this.barcodeRef}
                                            disabled={allScanned}
                                            value={this.state.barcodeValue}
                                            onChange={this.onScanInputChange}
                                            onKeyDown={this.onScanInputKeyDown}
                                        />
                                    </div>
                                    {/* Status + instruction merged into one highlighted line now,
                                        instead of a separate small "Ready to scan" pill above plus
                                        a muted hint here - one clear call-to-action, not two. */}
                                    <div className={"mtl-status-line" + (allScanned ? " mtl-done" : ((this.state.isOffline || ackPending) ? " mtl-ready" : (this.state.scanPhase === 'processing' ? " mtl-scanning" : " mtl-ready")))} style={{ width: '100%', margin: '0 0 12px' }}>
                                        <span className="mtl-status-dot"></span>
                                        <span>{allScanned ? 'All cartons scanned' : (this.state.isOffline ? 'Offline - scanning paused' : (ackPending ? 'Tap OK above to continue scanning' : (this.state.scanPhase === 'processing' ? 'Processing…' : 'Point the scanner and pull the trigger')))}</span>
                                    </div>
                                    {/* Camera is a Scan-mode-only input source now - a second scan
                                        source alongside the hardware wedge/global capture. It
                                        doesn't fit Manual Entry, which is deliberately the one place
                                        a human types instead of scanning at all. */}
                                    <button className="mtl-export-btn" type="button" disabled={scanBlocked} onClick={this.openCamera} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path><circle cx="12" cy="13" r="4"></circle></svg>
                                        Scan with Camera
                                    </button>
                                </>
                            )}
                        </div>

                        <div className="mtl-progress-panel">
                            <div className="mtl-progress-head">
                                <span>Loading Progress</span>
                                <span className="mtl-progress-pct">{pct}%</span>
                            </div>
                            <div className="mtl-progress-track"><div className="mtl-progress-fill" style={{ width: pct + '%' }}></div></div>
                            <div className="mtl-progress-sub"><b>{s.loaded}</b> of <b>{expected}</b> cartons expected</div>
                        </div>

                        <div className="mtl-chip-row">
                            <div className="mtl-chip mtl-chip-loaded">
                                <span className="mtl-chip-icon"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"></path></svg></span>
                                <span className="mtl-chip-text"><span className="mtl-chip-num">{s.loaded}</span><span className="mtl-chip-lbl">Loaded</span></span>
                            </div>
                            <span className="mtl-chip-sep">|</span>
                            <div className="mtl-chip mtl-chip-remaining">
                                <span className="mtl-chip-icon"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg></span>
                                <span className="mtl-chip-text"><span className="mtl-chip-num">{remaining}</span><span className="mtl-chip-lbl">Remaining</span></span>
                            </div>
                        </div>

                        <div className={"mtl-accordion" + (this.state.missingOpen ? " mtl-open" : "")}>
                            <button className="mtl-accordion-header" type="button" onClick={() => this.setState((p) => ({ missingOpen: !p.missingOpen }))}>
                                <span className="mtl-accordion-title">Missing HUs <span className="mtl-accordion-count">{remaining}</span> <span className="mtl-accordion-count">{uniqueOrders} {uniqueOrders === 1 ? 'Order' : 'Orders'}</span></span>
                                <svg className="mtl-accordion-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
                            </button>
                            <div className="mtl-accordion-body">
                                {remaining === 0 ? (
                                    <div className="mtl-missing-item">All cartons accounted for.</div>
                                ) : s.remainingHUs.map(hu => (
                                    <div className="mtl-missing-item" key={hu.id}>{hu.id}<br /><b>{hu.consignee} | {hu.sub}</b></div>
                                ))}
                            </div>
                        </div>
                    </div>
                <div className="mtl-actions2">
                    <div className="mtl-bottom-row">
                        <button className="mtl-export-btn" disabled={this.state.viewingSummary} onClick={this.viewSummary} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                            {this.state.viewingSummary && <span className="mtl-spinner"></span>}
                            {this.state.viewingSummary ? 'Loading…' : 'View Summary'}
                        </button>
                    </div>
                </div>
                {this.state.cameraOpen && (
                    <div style={{ position: 'fixed', inset: 0, background: '#000', zIndex: 2000, display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', color: '#fff', flex: '0 0 auto' }}>
                            <span style={{ fontWeight: 600, fontSize: '14px' }}>Aim at the barcode</span>
                            <button type="button" onClick={this.closeCamera} aria-label="Close camera" style={{ background: 'none', border: 'none', color: '#fff', fontSize: '26px', lineHeight: 1, padding: '4px 8px' }}>&times;</button>
                        </div>
                        <div style={{ position: 'relative', flex: '1 1 auto', width: '100%', height: '100%', overflow: 'hidden' }}>
                            <video ref={this.videoRef} muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            {/* Purely a visual aim guide - ZXing decodes the full video
                                frame regardless of this box, it's not an actual crop/
                                decode-region restriction. Wide rectangle (not square)
                                since Code 128/39 labels here are horizontal 1D barcodes,
                                not QR. Previously there was NO visual target at all -
                                just the video feed and a text label - giving the
                                operator nothing to actually line the barcode up against.
                                The box-shadow spotlight (huge spread, dark color) dims
                                everything OUTSIDE this rect without a separate overlay
                                element/cutout mask - a common trick for the "rest of the
                                screen is dulled" look modern QR/UPI scanners use. */}
                            {/* Only once the stream is live - see cameraStreaming's
                                comment in the constructor. */}
                            {this.state.cameraStreaming && !this.state.cameraError && (
                            <div className="mtl-scan-frame" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '78%', maxWidth: '360px', height: '110px', pointerEvents: 'none' }}>
                                <div className="mtl-scan-frame-mask">
                                    <div className="mtl-scan-line" />
                                </div>
                                {[
                                    { top: 0, left: 0, borderWidth: '4px 0 0 4px', borderRadius: '10px 0 0 0' },
                                    { top: 0, right: 0, borderWidth: '4px 4px 0 0', borderRadius: '0 10px 0 0' },
                                    { bottom: 0, left: 0, borderWidth: '0 0 4px 4px', borderRadius: '0 0 0 10px' },
                                    { bottom: 0, right: 0, borderWidth: '0 4px 4px 0', borderRadius: '0 0 10px 0' }
                                ].map((corner, i) => (
                                    <div key={i} className="mtl-scan-corner" style={{ position: 'absolute', width: '28px', height: '28px', borderStyle: 'solid', ...corner }} />
                                ))}
                            </div>
                            )}
                        </div>
                        {/* The full-screen camera sits above the normal alert banner
                            (that one lives in .mtl-screen-scroll, underneath this
                            overlay), so with the camera staying open across scans now,
                            this is the only visual readout an operator gets per scan -
                            playScanFeedback's vibration/beep is the other half of it.
                            needsAck alerts (exceptions) skip this pill entirely - the
                            full-screen acknowledgeModal below renders above this whole
                            camera view (higher z-index) and handles those instead. */}
                        {alert.show && !alert.needsAck && (
                            <div style={{
                                position: 'absolute', bottom: '90px', left: '50%', transform: 'translateX(-50%)',
                                background: alert.cls === 'valid' ? 'rgba(46,125,50,0.92)' : 'rgba(0,0,0,0.75)',
                                color: '#fff', padding: '10px 18px', borderRadius: '10px', maxWidth: '85%', textAlign: 'center',
                                fontSize: '13px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px'
                            }}>
                                <span style={{ fontSize: '16px' }}>{ALERT_ICONS[alert.cls] || '•'}</span>
                                <span>{alert.title}</span>
                            </div>
                        )}
                        {this.state.cameraError && (
                            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: '#fff', textAlign: 'center', padding: '16px', maxWidth: '85%' }}>
                                {this.state.cameraError}
                            </div>
                        )}
                    </div>
                )}
                {/* Full-screen, compulsory acknowledgement for an exception
                    (duplicate/wrong-truck/not-found/network-error/reassigned) - OK
                    is the ONLY way out, by design. Offline is the one exception to
                    that: its button is Retry Connection instead (see the button's
                    own conditional below and retryConnection's comment) - a manual
                    safety net alongside the automatic dismiss (onGoOnline), not a
                    plain acknowledgement, since there's nothing here for the
                    operator to actually attest to beyond "is it back yet".
                    submitBarcode's own ackPending guard is what
                    actually refuses another scan attempt; this backdrop is what
                    blocks literally everything else on screen while one is
                    pending: MobileHeader's hamburger/back button and warehouse
                    switcher (a sibling of this component, not a child - but a
                    fixed, viewport-covering backdrop sits above it regardless of
                    where it lives in the tree), the accordions, View Summary, the
                    entry-mode tabs, and the full-screen camera view's own Close
                    button (z-index 2000) - all physically covered by this one
                    layer (z-index 3000), so nothing under it can receive a click
                    at all; no need to individually disable each of them. */}
                {ackPending && (
                    <div style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(15,23,42,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
                        <div style={{ width: '100%', maxWidth: '340px', background: '#fff', borderRadius: '16px', padding: '24px 20px', textAlign: 'center', boxShadow: '0 12px 32px rgba(0,0,0,0.35)' }}>
                            <div style={{ fontSize: '34px', lineHeight: 1, marginBottom: '10px', color: alert.cls === 'duplicate' ? '#e65100' : alert.cls === 'wrong' ? '#c62828' : alert.cls === 'notfound' ? '#6a1b9a' : '#333' }}>
                                {ALERT_ICONS[alert.cls] || '•'}
                            </div>
                            <div style={{ fontWeight: 800, fontSize: '15px', color: '#1a1a1a', marginBottom: alert.sub ? '6px' : '18px' }}>{alert.title}</div>
                            {alert.sub && <div style={{ fontSize: '13px', color: '#555', marginBottom: '18px' }}>{alert.sub}</div>}
                            {/* The offline alert gets Retry Connection instead of a plain OK -
                                see retryConnection's own comment for why: the automatic
                                dismiss (onGoOnline, the real 'online' event) is still the
                                primary path, this is only a manual safety net that actually
                                re-checks the connection instead of dismissing on faith. Every
                                other exception (duplicate/wrong-truck/not-found/network-error/
                                reassigned) keeps the plain OK - those need a deliberate,
                                unconditional acknowledgement, not a connectivity check. */}
                            <button
                                type="button"
                                onClick={alert.feedbackType === 'offline' ? this.retryConnection : this.acknowledgeAlert}
                                style={{ width: '100%', padding: '12px', borderRadius: '10px', border: 'none', background: '#1976d2', color: '#fff', fontWeight: 800, fontSize: '14px', cursor: 'pointer' }}
                            >{alert.feedbackType === 'offline' ? 'Retry Connection' : 'OK'}</button>
                        </div>
                    </div>
                )}
            </>
        );
    }
}
